import {
  DashboardState,
  EDesignerEntityKind,
  EScreenType,
  ESdChangeReason,
  IScreenDesignInfo
} from "@kuika/kuika-cl-model";
import * as React from "react";
import { connect } from "react-redux";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  hasRecentlyModified,
  RECENTLY_MODIFIED_TAB,
  RECENTLY_MODIFIED_TAB_TEST_ID,
  resolveActiveTab,
  selectRecentlyModified
} from "../../../shared/utility/recently-modified";
import { buildLastModifiedColumn } from "../../../shared/utility/recently-modified-columns";
import {
  createFeedRequestGuard,
  haveItemIdsChanged,
  loadModificationFeed,
  LOADING_FEED,
  type IFeedRequestGuard,
  type IModificationFeed
} from "../../../shared/utility/recently-modified-feed";
import {
  buildScreenRowActions,
  canOpenDeleteScreenModal,
  deleteScreenFromRow,
  duplicateScreenFromRow,
  exportScreenKuikaFile,
  getScreenActionAccess,
  getScreenTypeOf,
  IScreenActionAccess,
  renameCurrentScreen,
  ScreenRenameOutcome
} from "../../../shared/utility/screen-row-actions";
import { showDomainAccessRequiredModal } from "../../../store/domain-access-modal/actions";
import { DeleteModal } from "../../common/delete-modal";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaListPage, {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableRowAction,
  KuikaListTableStatusCell
} from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import { ReportCreateModal } from "./report-create-modal";
import "./create-report-design.scss";

/**
 * The Report Designer landing page: the project's reports as a table, above the card that
 * creates one.
 *
 * It used to be a hero with a name field — the only thing the centre of the tool ever
 * showed until a report was open — while the reports themselves lived exclusively in the
 * left panel. It is now built from `KuikaListPage`, like Process Automation, AI Functions
 * and the Email Builder, so the tool opens on its own contents.
 *
 * The Reports LEFT PANEL stays: unlike those three, it is not a panel of its own but
 * `screens-panel/screens-window.tsx` in `designType="report"` mode — the Screens panel's
 * own file. This page therefore ADDS a surface rather than replacing one, and every row
 * action it offers is the panel's, from `shared/utility/screen-row-actions.tsx`.
 *
 * Columns are what a report actually carries: `sd.screenList` is `IScreenDesignInfo`
 * (id, name, publicMetaData) — there is no created/updated timestamp and no author on it.
 * `GetAllByProjectId` has them but reads every screen's JSON blob plus a per-screen history
 * query, so it is not a source for this table either. When a report was last written, and by
 * whom, therefore comes from the shared entity-modification feed — which is what the
 * "Recently modified" tab the page opens on, and the last two columns, both read.
 */

/** The tab holding every report, beside the "Recently modified" one the page opens on. */
const LIST_TAB: string = "Reports";

export interface ICreateReportDesignerState {
  /** The tab the user chose, or null while the page is still deciding for itself. */
  pickedTab: string | null;
  feed: IModificationFeed;
  isCreating: boolean;
  isNameModalVisible: boolean;
  searchText: string;
  renamingReportId: string | null;
  reportToReveal: string | null;
  renameValue: string;
  isRenameSaving: boolean;
  reportToDelete: IScreenDesignInfo | null;
  isDeleteModalVisible: boolean;
}

export interface ICreateReportDesignerProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
}

type CreateReportDesignerProps = ICreateReportDesignerProps & IWithSDProps & IWithContextProp;

class CreateReportDesigner extends PureSdComponent<CreateReportDesignerProps, ICreateReportDesignerState> {
  private isMounted_: boolean = false;
  /** Guards `loadFeed` against an out-of-order reply when the item set changes twice in a row. */
  private feedGuard_: IFeedRequestGuard = createFeedRequestGuard();

  constructor(props: CreateReportDesignerProps) {
    super(props);
    this.state = {
      pickedTab: null,
      feed: LOADING_FEED,
      isCreating: false,
      isNameModalVisible: false,
      searchText: "",
      renamingReportId: null,
      reportToReveal: null,
      renameValue: "",
      reportToDelete: null,
      isRenameSaving: false,
      isDeleteModalVisible: false
    };
  }

  /**
   * The list is the shared `sd.screenList`, so it is usually already loaded by whatever
   * opened the project. Fetch it only when it is empty rather than depending on the sibling
   * Reports panel having done it — this page renders with or without that panel.
   */
  componentDidMount = (): void => {
    this.isMounted_ = true;
    if (!this.props.sd?.screenList || this.props.sd.screenList.length === 0) {
      void this.props.sd?.setScreenList();
    }
    void this.loadFeed();
    document.body.dataset.reportDesignerLanding = "true";
  };

  /**
   * The feed answers for every screen in the project, not only the reports — there is one kind for
   * all of them. Rows are intersected by id against this page's report-only list, so the surplus
   * falls away without the page having to ask for a narrower scope than the endpoint offers.
   */
  loadFeed = async (): Promise<void> => {
    const token: number = this.feedGuard_.start();
    const projectId: string | undefined = this.props.sd?.project?.id;
    const feed: IModificationFeed = await loadModificationFeed([EDesignerEntityKind.ScreenDesign], projectId);
    if (!this.isMounted_ || !this.feedGuard_.isCurrent(token)) return;
    this.setState({ feed });
  };

  /**
   * The page stays mounted through a duplicate (`handleDuplicate`) and a create started
   * elsewhere, so `screenList` can gain or lose a report without this page ever remounting to
   * re-fetch the feed on its own. The id set is the smallest correct signal: it fires on a
   * create/duplicate/delete and stays quiet on an in-place rename, which this page reads off the
   * row itself rather than off the feed.
   */
  componentDidUpdate = (prevProps: Readonly<CreateReportDesignerProps>): void => {
    if (haveItemIdsChanged(this.getReports(prevProps), this.getReports(), (row: IScreenDesignInfo) => row.id)) {
      void this.loadFeed();
    }
  };

  handleTabChange = (tab: string): void => {
    this.setState({ pickedTab: tab });
  };

  componentWillUnmount = (): void => {
    this.isMounted_ = false;
    delete document.body.dataset.reportDesignerLanding;
  };

  // --- Data ------------------------------------------------------------------

  getReports = (props: CreateReportDesignerProps = this.props): IScreenDesignInfo[] =>
    (props.sd?.screenList ?? []).filter(
      (screen: IScreenDesignInfo): boolean => getScreenTypeOf(screen) === EScreenType.Report
    );

  /**
   * Search matching is the Reports panel's, transliteration included, so a query surfaces
   * the same reports on both surfaces. Rows come out name-sorted like the panel's list;
   * header sorting takes over from there.
   */
  getFilteredReports = (): IScreenDesignInfo[] => {
    const searchText: string = KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase();
    const matching: IScreenDesignInfo[] = this.getReports().filter((screen: IScreenDesignInfo): boolean =>
      searchText.length === 0 ? true : screen.name.toLowerCase().indexOf(searchText) !== -1
    );
    return matching.sort((a: IScreenDesignInfo, b: IScreenDesignInfo): number =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
    );
  };

  /** The reports the feed dates inside its window, newest first — the same rule on every landing. */
  getRecentReports = (): IScreenDesignInfo[] =>
    selectRecentlyModified<IScreenDesignInfo>({
      items: this.getFilteredReports(),
      index: this.state.feed.index,
      getId: (row: IScreenDesignInfo) => row.id,
      getName: (row: IScreenDesignInfo) => row.name ?? "",
      now: Date.now()
    });

  /**
   * Derived rather than stored, so it cannot go stale against the feed: the page opens on the
   * recent tab, drops to the full list when nothing is recent or the feed failed, and otherwise
   * shows whatever the user picked.
   */
  getActiveTab = (): string =>
    resolveActiveTab({
      pickedTab: this.state.pickedTab,
      feedStatus: this.state.feed.status,
      // Asked of every report, not the searched ones: a query that matches nothing recent must not
      // move the user to another tab mid-keystroke.
      hasRecent: hasRecentlyModified<IScreenDesignInfo>(
        this.getReports(),
        this.state.feed.index,
        (row: IScreenDesignInfo) => row.id,
        Date.now()
      ),
      // This page fetches its own list when the shared one is empty, so the feed can answer first.
      // Deciding then would read "nothing is recent" off an empty list and drop to the Reports tab,
      // only to jump back when the screens land.
      itemsReady: this.isListReady(),
      recentTab: RECENTLY_MODIFIED_TAB,
      fallbackTab: LIST_TAB
    });

  isListReady = (): boolean => (this.props.sd?.screenList?.length ?? 0) > 0;

  /** The page is still choosing its own tab: nobody has picked one, and what it chooses from is on its way. */
  isChoosingTab = (): boolean =>
    this.state.pickedTab === null && (this.state.feed.status === "loading" || !this.isListReady());

  getParameterCount = (screen: IScreenDesignInfo): number => screen.publicMetaData?.reportParameters?.length ?? 0;

  isCurrentReport = (screen: IScreenDesignInfo): boolean => screen.id === KMainFunctions.getSdIdFromUrl();

  // --- Create ----------------------------------------------------------------

  openNameModal = (): void => {
    this.setState({ isNameModalVisible: true });
  };

  closeNameModal = (): void => {
    this.setState({ isNameModalVisible: false });
  };

  // --- Open ------------------------------------------------------------------

  /**
   * Opening a report saves the screen that is currently in the URL first, exactly as the
   * Reports panel does — `loadScreen` navigates and reopens the project, so anything
   * unsaved on the outgoing screen would be lost.
   */
  handleOpenReport = async (screen: IScreenDesignInfo): Promise<void> => {
    // While the rename editor is open, a row click is the click that commits it; letting it
    // also navigate would race the save.
    if (this.state.renamingReportId) return;
    await this.props.sd.saveScreen(true);
    // `loadScreen` no-ops when the id is already the one in the URL — which it usually IS on
    // this page, since entering the tool leaves the last report loaded and shows this list on
    // top of it. That no-op is what left the canvas blank after a round trip through another
    // tool: `canvas.tsx` renders a different subtree for the Report Designer, so leaving the
    // tool unmounts `CanvasFrame`, whose `componentWillUnmount` deliberately clears the design
    // out of the agent iframe. Nothing pushes it back unless the screen is actually reloaded,
    // so ask for the reload explicitly when reopening the report already in the URL.
    const isAlreadyInUrl: boolean = KMainFunctions.getSdIdFromUrl() === screen.id;
    void this.props.sd.loadScreen(screen.id, isAlreadyInUrl);
    this.props.sd.setVisibilityOfCreateReportDesignPage(false);
  };

  // --- Inline rename ---------------------------------------------------------
  // Rename is `saveScreen(true, newName)`, which acts on the screen loaded in the canvas —
  // that is why the action only shows on the open report, here and in the panel alike.

  startRename = (screenId: string): void => {
    const target: IScreenDesignInfo | undefined = this.getReports().find(
      (screen: IScreenDesignInfo): boolean => screen.id === screenId
    );
    if (!target) return;
    this.setState({ renamingReportId: screenId, renameValue: target.name });
  };

  cancelRename = (): void => {
    this.setState({ renamingReportId: null, renameValue: "", isRenameSaving: false });
  };

  commitRename = async (): Promise<void> => {
    if (!this.state.renamingReportId || this.state.isRenameSaving) return;
    this.setState({ isRenameSaving: true });
    const renamedId: string = this.state.renamingReportId;
    const outcome: ScreenRenameOutcome = await renameCurrentScreen(this.props.sd, this.state.renameValue);
    if (outcome === "rejected") {
      this.setState({ isRenameSaving: false });
      return;
    }
    this.cancelRename();
    // The list is name-sorted, so the new name can put the report on another page. Ask for
    // it only once the refreshed list is in, or the reveal would look up the old order.
    await this.props.sd.setScreenList();
    this.setState({ reportToReveal: renamedId });
  };

  handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      void this.commitRename();
      return;
    }
    if (e.key === "Escape") {
      this.cancelRename();
    }
  };

  // --- Duplicate / delete ----------------------------------------------------

  handleDuplicate = (screen: IScreenDesignInfo): void => {
    duplicateScreenFromRow(this.props.sd, screen.id, screen.name, this.props.showDomainAccessRequiredModal, {
      // The duplicate appears as a row in this very table, so the page stays where it is
      // instead of falling through to whatever screen the canvas still holds.
      closeCreatePage: false,
      onCompleted: () => void this.props.sd.setScreenList()
    });
  };

  handleRequestDelete = (screen: IScreenDesignInfo): void => {
    if (!canOpenDeleteScreenModal(this.props.sd)) return;
    this.setState({ reportToDelete: screen, isDeleteModalVisible: true });
  };

  closeDeleteModal = (): void => {
    this.setState({ reportToDelete: null, isDeleteModalVisible: false });
  };

  deleteReport = async (): Promise<void> => {
    if (!this.state.reportToDelete) return;
    await deleteScreenFromRow(
      this.props.sd,
      this.state.reportToDelete,
      "report",
      () => void this.props.sd.setScreenList()
    );
  };

  // --- Table -----------------------------------------------------------------

  renderRenameEditor = (): React.ReactNode => (
    // The editor replaces only the name text; the icon tile stays so the row doesn't jump.
    <span
      className="kuika-list-table__name-cell kuika-report-designer__rename"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <span className="kuika-list-table__name-icon">
        <KuikaSvg name="file-text" width={14} height={14} />
      </span>
      <KuikaInput
        size="sm"
        autoFocus
        value={this.state.renameValue}
        disabled={this.state.isRenameSaving}
        onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
          this.setState({ renameValue: String(value ?? "") })
        }
        onKeyDown={this.handleRenameKeyDown}
        onBlur={() => void this.commitRename()}
        data-testid="renameInput"
      />
    </span>
  );

  getColumns = (): KuikaListTableColumn<IScreenDesignInfo>[] => [
    {
      id: "name",
      header: () => "Name",
      accessorFn: (row: IScreenDesignInfo) => row.name?.toLowerCase() ?? "",
      cell: (row: IScreenDesignInfo) =>
        this.state.renamingReportId === row.id ? (
          this.renderRenameEditor()
        ) : (
          <span
            className="kuika-report-designer__name-cell"
            data-testid={`${row.name}ReportRow`}
            style={
              {
                "--kuika-list-table-icon-bg": "var(--blue-1)",
                "--kuika-list-table-icon-color": "var(--accent-fg)"
              } as React.CSSProperties
            }
          >
            <KuikaListTableNameCell name={row.name} icon={<KuikaSvg name="file-text" width={14} height={14} />} />
          </span>
        )
    },
    {
      id: "parameters",
      header: () => "Parameters",
      width: 160,
      accessorFn: (row: IScreenDesignInfo) => this.getParameterCount(row),
      cell: (row: IScreenDesignInfo) => {
        const count: number = this.getParameterCount(row);
        if (count === 0) {
          return <KuikaSmallText className="kuika-report-designer__muted">—</KuikaSmallText>;
        }
        return <KuikaSmallText>{count === 1 ? "1 parameter" : `${count} parameters`}</KuikaSmallText>;
      }
    },
    {
      id: "status",
      header: () => "Status",
      width: 130,
      enableSorting: false,
      cell: (row: IScreenDesignInfo) =>
        this.isCurrentReport(row) ? <KuikaListTableStatusCell label="Open" color="green" /> : null
    },
    buildLastModifiedColumn<IScreenDesignInfo>(this.state.feed.index, (row: IScreenDesignInfo) => row.id)
  ];

  getRowActions = (): KuikaListTableRowAction<IScreenDesignInfo>[] =>
    buildScreenRowActions({
      sd: this.props.sd,
      username: this.props.context?.userInfo?.username,
      isCurrentScreen: this.isCurrentReport,
      handlers: {
        onRename: (screenId: string) => this.startRename(screenId),
        onDuplicate: (screen: IScreenDesignInfo) => this.handleDuplicate(screen),
        onExport: () => exportScreenKuikaFile(this.props.sd),
        onDelete: (screen: IScreenDesignInfo) => this.handleRequestDelete(screen)
      }
    });

  getEmptyTitle = (isNoSearchMatch: boolean, isEmptyRecents: boolean): string => {
    if (isNoSearchMatch) return "No report found";
    if (isEmptyRecents) return "Nothing changed recently";
    return "No reports yet";
  };

  getEmptyDescription = (isNoSearchMatch: boolean, isEmptyRecents: boolean): string => {
    if (isNoSearchMatch) return "We couldn't find any items matching your search.";
    if (isEmptyRecents) {
      // A failed feed is said out loud rather than shown as "nothing happened", which would be a
      // claim the page cannot actually make.
      return this.state.feed.status === "failed"
        ? "We couldn't load recent activity — open Reports to see every report."
        : "Reports edited in the last 30 days appear here.";
    }
    return "Create a report to send dynamic content to your users.";
  };

  renderTable = (): React.ReactNode => {
    const isRecentTab: boolean = this.getActiveTab() === RECENTLY_MODIFIED_TAB;
    const rows: IScreenDesignInfo[] = isRecentTab ? this.getRecentReports() : this.getFilteredReports();
    const hasReports: boolean = this.getReports().length > 0;
    const isSearching: boolean = this.state.searchText.length > 0;
    const rowActions: KuikaListTableRowAction<IScreenDesignInfo>[] = this.getRowActions();
    const isNoSearchMatch: boolean = hasReports && isSearching;
    // The recent tab is only reached with nothing in it when the user picked it deliberately,
    // or when a search emptied it — the page opens on the full list otherwise.
    const isEmptyRecents: boolean = isRecentTab && rows.length === 0 && !isSearching;
    // The page parks on the recent tab while the feed is in flight. Without this it would paint
    // an empty table first and claim nothing had changed recently — something it cannot yet know.
    const isFeedPending: boolean = isRecentTab && this.state.feed.status === "loading";

    return (
      <KuikaListTable<IScreenDesignInfo>
        data={rows}
        loading={isFeedPending && rows.length === 0}
        columns={this.getColumns()}
        // A viewer with no screen access gets no menu at all rather than an empty column.
        rowActions={rowActions.length > 0 ? rowActions : undefined}
        rowActionsTestId={(row: IScreenDesignInfo) => `${row.name}ReportRowMenu`}
        rowKey={(row: IScreenDesignInfo) => row.id}
        revealRowKey={this.state.reportToReveal}
        onRowRevealed={() => this.setState({ reportToReveal: null })}
        onRowClick={(row: IScreenDesignInfo) => void this.handleOpenReport(row)}
        emptyIcon={
          <KuikaSvg
            name={isNoSearchMatch ? "search-off" : "report-designer"}
            type={isNoSearchMatch ? "common" : "topbar-icons"}
            width={32}
            height={32}
          />
        }
        emptyVariant="page"
        emptyTitle={this.getEmptyTitle(isNoSearchMatch, isEmptyRecents)}
        emptyDescription={this.getEmptyDescription(isNoSearchMatch, isEmptyRecents)}
        data-testid="reportDesignerTable"
      />
    );
  };

  render() {
    if (!this.props.sd || this.props.sd.dashboardState !== DashboardState.reportDesigner) {
      return null;
    }

    const access: IScreenActionAccess = getScreenActionAccess(this.props.sd);

    return (
      <>
        {/* The canvas below stays mounted while this page shows, so the page fills the
            centre column rather than laying out beside it. The design iframe it hosts is a
            body-level element and cannot be covered by anything in the column, so it is
            hidden by the `data-report-designer-landing` flag this component sets on <body>
            (see the stylesheet) rather than by paint order. */}
        <KuikaListPage
          fill
          busy={this.state.isCreating}
          className="kuika-report-designer"
          data-testid="reportDesignerScreen"
        >
          <KuikaListPageHeader
            icon={<KuikaSvg name="report-designer" type="topbar-icons" width={20} height={20} />}
            title="Report Designer"
            titleTestId="reportDesignerTitle"
          />

          {access.canCreateScreen && (
            <KuikaCreateCardRow>
              <KuikaCreateCard
                title="Create a blank report"
                description="Send data-driven reports to your users or offer them as downloads."
                icon={<KuikaSvg name="plus" width={20} height={20} />}
                iconBackground="var(--blue-1)"
                onClick={this.openNameModal}
                loading={this.state.isCreating}
                data-testid="reportDesignerCreateCard"
              />
            </KuikaCreateCardRow>
          )}

          <KuikaListPageToolbar
            tabs={
              <KuikaTabs
                size="lg"
                options={[
                  // `label` is not optional in practice: KuikaTabs renders `icon || label`, so an
                  // option carrying only a value draws an empty pill.
                  {
                    value: RECENTLY_MODIFIED_TAB,
                    label: RECENTLY_MODIFIED_TAB,
                    dataTestId: RECENTLY_MODIFIED_TAB_TEST_ID
                  },
                  { value: LIST_TAB, label: LIST_TAB, dataTestId: "reportsTab" }
                ]}
                // Remounted once when the page stops choosing its own tab, so the tab it settles on
                // is the control's first value and the pill does not slide there from "Recently
                // modified" — on most visits nothing is recent, and every one would open on a slide.
                // Every switch after that slides as before. Config Manager does the same for its
                // content panes; here the pill is what moves.
                key={this.isChoosingTab() ? "choosing" : "chosen"}
                value={this.getActiveTab()}
                onChange={this.handleTabChange}
                dataTestId="reportDesignerTabs"
              />
            }
            search={
              <KuikaInput
                size="md"
                placeholder="Search by name..."
                value={this.state.searchText}
                leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
                  this.setState({ searchText: String(value ?? "") })
                }
                data-testid="reportDesignerSearchInput"
              />
            }
          />

          {this.renderTable()}
        </KuikaListPage>

        {/* The page shows its own progress (`busy` above, and the card's spinner), so it
            follows the sheet's creating state rather than owning the create itself. */}
        <ReportCreateModal
          visible={this.state.isNameModalVisible}
          onClose={this.closeNameModal}
          onBusyChange={(isBusy: boolean): void => this.setState({ isCreating: isBusy })}
        />

        <DeleteModal
          itemName={this.state.reportToDelete?.name ?? ""}
          triggerModal={this.state.isDeleteModalVisible}
          deleteItem={() => this.deleteReport()}
          deleteType="report"
          closeModal={this.closeDeleteModal}
        />
      </>
    );
  }

  /**
   * The list this page renders is `sd.screenList`, which only ever changes through
   * `setScreenList` (`OtherSetStates`) or `setIsScreenWindowReload` (`ScreenReload`) —
   * both of which `PureSdComponent` already lets through before consulting this. Everything
   * else the context reports leaves the table identical, so it stops here.
   */
  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const createReportDesigner = connect(
  null,
  mapDispatchToProps
)(withContext<ICreateReportDesignerProps>(withSD<ICreateReportDesignerProps>(CreateReportDesigner)));

export { createReportDesigner as CreateReportDesigner };
